import type {Option} from '@/cart/types';
import {priceMappingSchema} from '@/experiments/price-test/types';
import {DeliveryOptionFilter} from '@/product/delivery-option';
import type {Image, ProductVariant} from '@/product/types';
import {ColorStyle} from '@/utilities/color-style-map';
import {z} from 'zod';

export type Badge = {
  color: ColorStyle;
  label: string;
};

export type BadgeSettings = {
  best_value_one_time_selector: SelectorItemWithBadgeSettings;
  best_value_pack_sizes: number[];
  most_popular_one_time_selector: SelectorItemWithBadgeSettings;
  most_popular_pack_sizes: number[];
};

/**
 * Represents the input for getting settings.
 * If the settings metaobject has references to other metaobjects, they will be followed
 * up to the number specified by followReferences.
 * @param handle - The handle of the metaobject to get from Shopify
 * @param followReferences - The number of nested references to follow
 * @param type - The type of the metaobject to get from Shopify
 */
export type GetSettingsInput = SettingsInputBase & {
  handle: string;
};

export type HasSelectedOptions = {
  selectedOptions: Option[];
};

/**
 * Represents the input for overriding settings.
 * If the settings metaobject has references to other metaobjects, they will be followed
 * up to the number specified by followReferences.
 * @param newHandle - The handle of the new metaobject to get from Shopify
 * @param oldHandle - The handle of the metaobject to be overridden
 * @param followReferences - The number of nested references to follow
 */
export type OverrideSettingsInput = SettingsInputBase & {
  newHandle: string;
  oldHandle: string;
};

/**
 * Represents the base input for settings.
 * Includes an optional follow references number and a type.
 */
type SettingsInputBase = {
  followReferences?: number;
  type: string;
};

/**
 * Represents the settings for the product select section.
 * These are configured as fields on the Shopify Metaobject.
 * We can use these settings to configure the appearance and behavior of the product select section.
 * Keep in mind that many of these settings are optional, and we should handle that gracefully.
 */
export type ProductSelectSectionSettings = {
  /**
   * Selector item with text representing the onetime option highlighted as 'Best Value' .
   * This can be used on the pack-size selector to highlight the best value pack size.
   */
  best_value_one_time_selector: SelectorItemWithBadgeSettings;
  /**
   * The pack size options to decorate with the "Best Value" badge in the delivery options step.
   * Any pack size option in this list should be decorated with the "Best Value" badge.
   */
  best_value_pack_sizes: number[];
  /**
   * The delivery option that should be selected by default. ie, Yearly Sub, Monthly Sub, One-Time.
   */
  default_delivery_option: DeliveryOptionFilter;
  /**
   * Label for the delivery interval in the 'Subscribe & Save' tab of the delivery options step.
   */
  delivery_interval_label: string;
  /**
   * The title to display on the delivery step. ie, 'Choose Your Delivery'
   */
  delivery_selector_title: string;
  /**
   * Image to display instead of the featured product image.
   * If not provided, we should fall back to the featured product image.
   */
  featured_image_override?: Image;
  /**
   * Whether the show decimal places when displaying price.
   * If true, format like this: $1.00
   * Else, format like this: $1
   */
  format_money_with_decimals?: boolean;
  /**
   * Label to display on the frequency helper button. ie, 'Which frequency is right for me?'
   */
  frequency_helper_button_label?: string;
  /**
   * List of options for the frequency helper form.
   * Use these as labels for the selector options.
   * Each option should map to a different subscription delivery option.
   */
  frequency_helper_options?: string[];
  /**
   * The title to display on the frequency helper form. ie, 'How large is your household?'
   */
  frequency_helper_title: string;
  /**
   * The label text to display next to the number of "Free" gifts unlocked.
   * ie, "Free gifts unlocked:"
   */
  gifts_unlocked_label: string;
  /**
   * List of product option names to ignore.
   * We should not offer the user the ability to select these options.
   * This is a protection against commonly misused product options such as 'Delivery' or 'Pack Size'.
   * These options are generally handled by delivery and pack size selectors instead.
   */
  ignore_options?: string[];
  /**
   * The selector item with badge representing the "Monthly Subscription" delivery option.
   * Use these settings to configure the selector item box, if applicable.
   */
  monthly_subscription_selector: SelectorItemWithBadgeSettings;
  /**
   * Selector item with text representing the onetime option highlighted as 'Most Popular' .
   * This can be used on the pack-size selector to highlight the most popular pack size.
   */
  most_popular_one_time_selector: SelectorItemWithBadgeSettings;
  /**
   * The pack size option(s) to decorate with the "Most Popular" badge in the delivery options step.
   * Any pack size option in this list should be decorated with the "Most Popular" badge.
   */
  most_popular_pack_sizes: number[];
  /**
   * The variants to use as one-time purchase options.
   * In general, they should have the same product options as the main product,
   * but there is no way to guarantee this.
   */
  multi_pack_variants: ProductVariant[];
  /**
   * The label text for the "next" button.
   * These should be mapped sequentially to the steps in the product select section.
   * ie, ['Next', 'Next', 'Next', 'Add to Cart']
   * Keep in mind that there may be more steps than labels, and we should handle that gracefully.
   */
  next_button_labels: string[];
  /**
   * The terms of the "Offer Text" offer. ie, if you subscribe by Jan. 17th
   */
  offer_terms: string;
  /**
   * The offer text displayed above the product options. Ie, "Save up to 40% + 3 Free Gifts"
   */
  offer_text: string;
  /**
   * Selector item with badge representing the "One-Time Purchase" delivery option.
   * Use these settings to configure the selector item box, if applicable.
   */
  one_time_purchase_selector: SelectorItemWithBadgeSettings;
  /**
   * A JSON array of settings for each product option.
   * These can be used to configure the appearance of the product option selectors.
   * Keep in mind that settings may not be available for every option, so we should handle that gracefully.
   */
  product_options_settings_map: ProductOptionSettingsMap;
  /**
   * Title to display above the product selector. If not provided, fall back to the product title.
   */
  product_title_override?: string;
  /**
   * The shipping guarantee text to display below the product select component.
   */
  shipping_guarantee: string;
  /**
   * Label text for the base product price. ie, 'Starting at'. If blank, do not display the label.
   */
  starting_at_label?: string;
  /**
   * The strategy for ordering the steps. e.g., if delivery_first, the delivery step is shown first.
   */
  step_order: 'delivery_first' | 'variant_first';
  /**
   * The subscription guarantee text to display on the delivery options step.
   */
  subscription_guarantee: string;
  /**
   * Text to display the terms of the subscription. ie, '100% Risk Free, Money Back Guarantee'
   */
  subscription_terms: string;
  /**
   * A list of image overrides for specific variants.
   * Variants not overridden should display featured variant image.
   */
  variant_image_overrides?: VariantImageOverride[];
  /**
   * Selector item with badge representing the "Yearly Subscription" delivery option.
   * Use these settings to configure the selector item box, if applicable.
   */
  yearly_subscription_selector: SelectorItemWithBadgeSettings;
  /**
   * The label text to display next to the savings percentage. Ie, "You're saving XX%"
   */
  youre_saving_label: string;
};

export type ProductSelectSettings = Omit<
  ProductSelectSectionSettings,
  'save_label'
>;

export type ProductOptionSettingsMapEntry = {
  color: string;
  description: string;
  icon?: Image;
  name: string;
  value: string;
};

export type ProductOptionSettingsMap = ProductOptionSettingsMapEntry[];

export type SelectorItemWithBadgeSettings = {
  badge_text?: string;
  color?: string;
  description?: string;
  label?: string;
};

export type VariantImageOverride = HasSelectedOptions & {
  image: Image;
};

export const optionSettingsMapEntrySchema = z.object({
  color: z.string(),
  description: z.string(),
  name: z.string(),
  value: z.string(),
  icon: z
    .object({
      url: z.string(),
      alt: z.string(),
    })
    .optional(),
});

export const steppedProductSelectorSettingsSchema = z.object({
  formatMoneyWithDecimals: z.boolean().nullable(),
  deliverySelectorTitle: z.string().nullable(),
  productTitleOverride: z.string().nullable(),
  productOptionsSettingsMap: z.array(optionSettingsMapEntrySchema).nullable(),
  priceMapping: z.array(priceMappingSchema).nullable().optional(),
  firstTimeDiscountCode: z.string().nullable().optional(),
  firstTimeOfferButtonLabel: z.string().nullable().optional(),
});

export type OptionSettingsMapEntry = z.infer<
  typeof optionSettingsMapEntrySchema
>;

export type SteppedProductSelectorSettings = z.infer<
  typeof steppedProductSelectorSettingsSchema
>;
